import md5 from 'md5';
import * as PIXI from 'pixi.js';
import type { Font, GlyphRun } from 'fontkit';
import type { WordStyle } from './WordStyle';
import { isBreakingSpace, isNewline } from './textMetricsHelpers';

/**
 * Computes the advance width for the given text.
 * Note that the text must be able to be rendered entirely by the provided font.
 */
export function computeAdvanceWidth(text: string, style: WordStyle, font: Font, run: GlyphRun): number {
    // New lines often are assigned the .notdef glyph by the font, which has a non-zero advance
    // width. However in our use case, new lines are never rendered and we always advance to the
    // next line. So we count new line chars as having zero advance width.
    if (isNewline(text)) {
        return 0;
    }

    return run.advanceWidth / font.unitsPerEm * style.fontSize;
}

/**
 * Computes the bounding box for the given text, taking into account the style.
 * Note that the text must be able to be rendered entirely by the provided font.
 */
export function computeBoundingBox(text: string, style: WordStyle, font: Font, run: GlyphRun): PIXI.Rectangle {
    // If there is no text then nothing to measure
    if (text.length === 0) {
        return PIXI.Rectangle.EMPTY;
    }

    // Since newlines and spaces are not actually visible to the user, we consider these
    // to always have a zero sized bounding box
    if (isNewline(text) || PIXI.TextMetrics.isBreakingSpace(text)) {
        return PIXI.Rectangle.EMPTY;
    }

    if (run.advanceWidth === 0) {
        // fontkit will return an infinite bbox for zero-sized text,
        // but our convention is to use an EMPTY bbox.
        return PIXI.Rectangle.EMPTY;
    }

    const boundingBox = run.bbox;

    let width = (boundingBox.maxX - boundingBox.minX) / font.unitsPerEm * style.fontSize;
    let height = (boundingBox.maxY - boundingBox.minY) / font.unitsPerEm * style.fontSize;
    let x = boundingBox.minX / font.unitsPerEm * style.fontSize;
    let y = boundingBox.minY / font.unitsPerEm * style.fontSize;

    // Styling doesn't impact the underlying glyph dimensions, but still needs to be accounted
    // for as there may be visual additions which make the text appear to cover more space.
    // These styles include drop shadow and stroke thickness.
    const shadowOffsetX = style.dropShadow ? style.dropShadowDistanceX : 0;
    const shadowOffsetY = (style.dropShadow ? style.dropShadowDistanceY : 0);

    // Stroke thickness is centered on the drawing pen, so it adds 1/2 of it's value
    // in every direction (top, right, bottom, and left), while the shadow offsets x & y
    // add their full value in one direction.
    const strokeOffset = style.strokeThickness;

    // Translate and extend bounds accordingly
    // Since the shadow only extends in the positive or negative x/y directions, in the
    // opposite x/y direction the dimensions grow by half of stroke width. Otherwise,
    // the stroke and shadow don't interact.
    width += Math.max(Math.abs(shadowOffsetX) + strokeOffset / 2, strokeOffset);
    height += Math.max(Math.abs(shadowOffsetY) + strokeOffset / 2, strokeOffset);

    // The x position is only affected by shadows with a negative x offset
    const shadowX = Math.min(shadowOffsetX, 0);

    // The y position is only affected by shadows with a positive offset (since y direction is
    // downwards for shadows)
    const shadowY = -Math.max(shadowOffsetY, 0);

    // Translation to account for stroke
    const strokeX = -strokeOffset / 2;
    const strokeY = -strokeOffset / 2;

    // Combine stroke and shadow offsets
    x += Math.min(shadowX, strokeX);
    y += Math.min(shadowY, strokeY);

    return new PIXI.Rectangle(x, y, width, height);
}

type SupportedGlyphRun = {
    /**
     * if font can render the text string
     */
    supported: boolean;
    /**
     * the glyph string of the text (without any newlines)
     */
    run: GlyphRun;
};

/**
 * Creates a glyph run from the given text and checks to make sure that there aren't any
 * undefined glyphs being used. Note that newline characters are excluded from the run.
 */
export function createAndCheckGlyphRun(text: string, font: Font): SupportedGlyphRun {
    // newline characters are often assigned the undefined glyph in a font, but
    // that doesn't break support so first remove these characters
    let cleanedText = '';
    for (let i = 0; i < text.length; i++) {
        if (isNewline(text[i])) {
            continue;
        }
        cleanedText += isBreakingSpace(text[i]) ? ' ' : text[i];
    }
    const run = createGlyphRun(cleanedText, font);
    const glyphs = run.glyphs;

    // the first glyph is used when a matching glyph can't be found -- represents not defined
    const supported = !glyphs.some(glyph => glyph.id === 0);

    return {
        supported,
        run,
    };
}


/**
 * Creates an object which stores a list of glyphs corresponding to a given string of text.
 * Note that there are a variety of details/options that can be tweaked here, but for now
 * we're just using the defaults.
 */
export function createGlyphRun(text: string, font: Font): GlyphRun {
    // Allow fontkit to control these for now
    const userFeatures = {};
    const script = null;
    const language = null;
    const direction = null;
    return font.layout(text, userFeatures, script, language, direction);
}

/**
 * The class name is simply the hash of the url – this guarantees a one way mapping
 * without requiring any additional information about the font with the stripped name
 * to help with debugging (also classes cannot start with numbers)
*/
export function generateCssFamilyName(fontUrl: string): string {
    if (fontUrl === null || fontUrl === undefined) {
        return '';
    }
    const filename = fontUrl.substring(fontUrl.lastIndexOf('/') + 1)
        .replace('.ttf', '')
        .replace('.otf', '')
        .replace(/[^a-z+]+/gi, '');
    return `${filename}-${md5(fontUrl).substr(0, 5)}`;
}
